import { useState, useEffect } from "react";
import { useAuth } from "./AuthContext";
import { Link, useNavigate } from "react-router-dom";
import { Lock, Zap, FileSpreadsheet, ArrowRight, Loader2, X, CheckCircle } from "lucide-react";
import axios from "axios";
import { analytics } from "./analytics";
import { API } from "./apiConfig";

export function PaywallModal({ isOpen, onClose, stats }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);

  useEffect(() => {
    if (isOpen) {
      analytics.paywallShown(user ? "credits_exhausted" : "signup_required");
    }
  }, [isOpen, user]);

  if (!isOpen) return null;

  const handleCheckout = async (packageId) => {
    if (!user) {
      navigate("/auth?mode=register");
      return;
    }
    analytics.checkoutStarted(packageId);
    setLoading(packageId);
    try {
      const origin = window.location.origin;
      const { data } = await axios.post(
        `${API}/payments/checkout`,
        { package_id: packageId, origin_url: origin },
        { withCredentials: true }
      );
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      console.error("Checkout error:", err);
      alert("Payment failed. Please try again.");
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="paywall-modal">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-[#0A1F3B]/60 backdrop-blur-2xl" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full max-w-lg bg-[#0A1F3B] border border-[#F5D300]/20 rounded-2xl p-8 shadow-[0_0_50px_-12px_rgba(245,211,0,0.15)]">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-[#475569] hover:text-white transition-colors"
          data-testid="paywall-close"
        >
          <X size={20} />
        </button>

        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-[#F5D300]/10 rounded-lg flex items-center justify-center">
            <Lock size={20} className="text-[#F5D300]" />
          </div>
          <div>
            <h2 className="font-heading text-xl font-bold text-white">
              Your payroll is ready
            </h2>
            <p className="text-sm text-[#94A3B8]">Unlock to download</p>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="bg-[#112A4A] border border-[#1C3D66] rounded-xl p-4 mb-5">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#94A3B8] mb-3">This run includes</p>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <p className="font-heading text-2xl font-black text-[#F5D300]">{stats.employees || 0}</p>
                <p className="text-xs text-[#94A3B8]">employees</p>
              </div>
              <div>
                <p className="font-heading text-2xl font-black text-[#F5D300]">{stats.totalShifts || 0}</p>
                <p className="text-xs text-[#94A3B8]">total shifts</p>
              </div>
              <div>
                <p className="font-heading text-2xl font-black text-[#F5D300]">{stats.overtime || 0}</p>
                <p className="text-xs text-[#94A3B8]">OT entries</p>
              </div>
            </div>
          </div>
        )}

        {/* Value reminder */}
        <div className="bg-[#F5D300]/5 border border-[#F5D300]/10 rounded-lg px-4 py-3 mb-6">
          <p className="text-sm text-[#94A3B8]">
            This would normally take <span className="text-white font-semibold">~45 minutes</span> to prepare manually.
          </p>
          <p className="text-sm text-[#F5D300] font-semibold mt-1">
            oly Payroll did it in seconds.
          </p>
        </div>

        {/* What you get */}
        <div className="space-y-2 mb-6">
          {["Overtime calculated automatically", "Break violations flagged", "Clean Excel export for your payroll provider"].map((item, i) => (
            <div key={i} className="flex items-center gap-2">
              <CheckCircle size={14} className="text-[#F5D300] flex-shrink-0" />
              <span className="text-xs text-[#94A3B8]">{item}</span>
            </div>
          ))}
        </div>

        {/* Action buttons */}
        {!user ? (
          <div className="space-y-3">
            <Link
              to="/auth?mode=register"
              className="flex items-center justify-center gap-2 w-full bg-[#F5D300] text-black font-bold rounded-lg py-3.5 hover:-translate-y-0.5 hover:shadow-[0_8px_20px_-4px_rgba(245,211,0,0.4)] transition-all duration-300"
              data-testid="paywall-signup-cta"
            >
              Create Free Account to Continue
              <ArrowRight size={18} />
            </Link>
            <p className="text-xs text-[#475569] text-center">
              You&apos;ve used your free run. Create an account to keep going.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Unlimited */}
            <button
              onClick={() => handleCheckout("unlimited_monthly")}
              disabled={loading === "unlimited_monthly"}
              className="flex items-center justify-center gap-2 w-full bg-[#F5D300] text-black font-bold rounded-lg py-3.5 hover:-translate-y-0.5 hover:shadow-[0_8px_20px_-4px_rgba(245,211,0,0.4)] transition-all duration-300 disabled:opacity-50"
              data-testid="paywall-unlimited-cta"
            >
              {loading === "unlimited_monthly" ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <>
                  Unlock Unlimited Access — $19/mo
                  <Zap size={18} />
                </>
              )}
            </button>

            {/* Single */}
            <button
              onClick={() => handleCheckout("single_export")}
              disabled={loading === "single_export"}
              className="flex items-center justify-center gap-2 w-full bg-[#1C3D66] text-white font-semibold rounded-lg py-3 hover:bg-[#1C3D66]/80 transition-colors disabled:opacity-50"
              data-testid="paywall-single-cta"
            >
              {loading === "single_export" ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <>
                  Download This Payroll — $5
                  <FileSpreadsheet size={16} />
                </>
              )}
            </button>

            <div className="flex items-center justify-center gap-4 text-[10px] text-[#475569] pt-1">
              <span>No contracts</span>
              <span>Cancel anytime</span>
              <span>Secure data</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
